/* eslint-disable max-lines */
import { GridColDef, GridColumnVisibilityModel, GridFilterOperator, getGridSingleSelectOperators } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from 'react';

import { CellAge } from './components/CellAge';
import { CellChip } from './components/CellChip';
import { CellColorCircle } from './components/CellColorCircle';
import { CellExamIcons } from './components/CellExamIcons';
import { CellIcon } from './components/CellIcon';
import { CellPdfButton } from './components/CellPdfButton';
import { CellUrgency } from './components/CellUrgency';
import { ClickableCell } from './components/ClickableCell';
import { ExamFilterInput } from './components/ExamFilterInput';
import { LinkCell } from './components/LinkRow';
import ReminderModal from './ReminderModal';
import i18n from 'FRONTEND/utils/i18n';

// Dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

type ExtendedGridColDef = GridColDef & {
  priority?: number // Used to determine priority of a column in the responsive column auto-hide algorithm.
};

const examAnyOfOperator: GridFilterOperator = {
  ...getGridSingleSelectOperators().find(op => op.value === 'isAnyOf')!,
  InputComponent: ExamFilterInput,
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value?.length) {
      return null;
    }
    return (value: string[]) => {
      if (!Array.isArray(value)) {
        return false;
      }
      return filterItem.value.some((v: string) => value.includes(v));
    };
  },
};

export const getConsultationColumns = ({
  config,
  isCCC = false,
  hasMultiProjects = false,
  hasMultiWorkflows = false,
  hasMultiTenants = false,
  userTimezone,
  onOpenReminderModal,
}): ExtendedGridColDef[] => {
  const columns: ExtendedGridColDef[] = [];
  switch (config) {
    case 'emirates':
      columns.push(
        { field: 'consultation_id', headerName: i18n._('ID'), priority: 1 },
        { field: 'created_at', headerName: i18n._('Creation'), priority: 2, flex: 1, filterable: false, valueFormatter: (value) => formatDateTime(value, userTimezone) },
        { field: 'creator_alias', headerName: i18n._('Origin'), priority: 3, flex: 1 },
        { field: 'creator_local', headerName: i18n._('User'), priority: 3, flex: 1 },
        { field: 'flight_number', headerName: i18n._('Flight No'), priority: 5 },
        { field: 'aircraft_registration', headerName: i18n._('Route'), priority: 6 },
        { field: 'seat_or_staff_number', headerName: i18n._('Seat No/Staff No'), priority: 7 },
        { field: 'patient_gender', headerName: i18n._('Gender'), priority: 14 },
        { field: 'patient_age', headerName: i18n._('Age'), priority: 15, renderCell: (params) => <CellAge age={params.value} /> },
        { field: 'station_vitals', headerName: i18n._('Exams'), priority: 16, filterable: false, flex: 1, renderCell: (params) => <CellExamIcons exams={params.value} /> },
        { field: 'last_event', headerName: i18n._('Last event'), priority: 9, width: 200, filterable: false, valueFormatter: (value) => formatDateTime(value, userTimezone) },
        { field: 'status_translated', headerName: i18n._('Status'), priority: 8, renderCell: (params) => <CellChip value={params.value} /> },
        {
          field: 'color_code',
          headerName: i18n._('Color code'),
          renderHeader: () => null,
          priority: 10,
          width: 20,
          filterable: false, // might be supported in the future.
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellColorCircle color={params.value} /></ClickableCell>,
        },
        {
          field: 'notes',
          headerName: i18n._('Notes'),
          renderHeader: () => null,
          priority: 11,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellIcon value={params.value} iconName='notes' userTimezone={userTimezone} /></ClickableCell>,
        },
        {
          field: 'reminder_at',
          headerName: i18n._('Reminder'),
          renderHeader: () => null,
          priority: 12,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellIcon value={params.value} iconName='reminder' userTimezone={userTimezone} /></ClickableCell>,
        },
        { field: 'links', headerName: i18n._('PDF'), renderHeader: () => null, priority: 13, sortable: false, filterable: false, width: 20, resizable: false, renderCell: (params) => <CellPdfButton links={params.value} /> },
        {
          field: 'modal',
          headerName: i18n._('Actions'),
          renderHeader: () => null,
          priority: 4,
          sortable: false,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ReminderModal row={params.row} showColorAndNotes />,
        },
      );
      break;

    case 'msf':
      columns.push(
        { field: 'consultation_id', headerName: i18n._('ID'), priority: 1, width: 20 },
        { field: 'created_at', headerName: i18n._('Creation'), priority: 8, width: 200, filterable: false, valueFormatter: (value) => formatDateTime(value, userTimezone) },
        ...(hasMultiProjects ? [
          { field: 'project_country', headerName: i18n._('Country'), priority: 10 },
          { field: 'project_name', headerName: i18n._('Project'), priority: 4 },
        ] : []),
        { field: 'first_ccc', headerName: i18n._('Initial CCC'), priority: 5, flex: 1 },
        { field: 'last_ccc', headerName: i18n._('Last CCC'), priority: 12, flex: 1 },
        { field: 'urgency', renderHeader: () => null, headerName: i18n._('Urgency'), priority: 6, filterable: false, width: 20, resizable: false, renderCell: (params) => <CellUrgency urgency={params.value} /> },
        { field: 'primary_specialty', headerName: i18n._('Primary Specialty'), priority: 9, flex: 1 },
        {
          field: 'unread',
          headerName: i18n._('Unread'),
          renderHeader: () => null,
          priority: 13,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <CellIcon value={params.value} iconName='unread' userTimezone={userTimezone} />,
        },
        { field: 'last_message', headerName: i18n._('Last Message'), priority: 11, sortable: true, filterable: false, width: 200, valueFormatter: (value) => formatDateTime(value, userTimezone) },
        { field: 'status_translated', headerName: i18n._('Status'), priority: 2, filterable: false, renderCell: (params) => <CellChip value={params.value} /> },
        { field: 'accept_ratio', headerName: i18n._('Accepted'), priority: 7, filterable: false },
        { field: 'station_vitals', headerName: i18n._('Attachments'), priority: 14, filterable: false, flex: 1, renderCell: (params) => <CellExamIcons exams={params.value} /> },
        ...(isCCC ? [
          {
            field: 'color_code',
            headerName: i18n._('Color code'),
            renderHeader: () => null,
            priority: 16,
            filterable: false,
            width: 20,
            resizable: false,
            renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellColorCircle color={params.value} /></ClickableCell>,
          },
          {
            field: 'notes',
            headerName: i18n._('Notes'),
            renderHeader: () => null,
            priority: 17,
            filterable: false,
            width: 20,
            resizable: false,
            renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellIcon value={params.value} iconName='notes' userTimezone={userTimezone} /></ClickableCell>,
          },
        ] : []),
        {
          field: 'reminder_at',
          headerName: i18n._('Reminder'),
          renderHeader: () => null,
          priority: 15,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellIcon value={params.value} iconName='reminder' userTimezone={userTimezone} /></ClickableCell>,
        },
        {
          field: 'modal',
          headerName: i18n._('Actions'),
          renderHeader: () => null,
          priority: 3,
          sortable: false,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ReminderModal row={params.row} showColorAndNotes={isCCC} />,
        },
      );
      break;

    default:
      columns.push(
        { field: 'consultation_id', headerName: i18n._('ID'), priority: 1, renderCell: (params) => <LinkCell {...params} /> },
        { field: 'created_at', headerName: i18n._('Creation'), priority: 2, flex: 1, filterable: false, valueFormatter: (value) => formatDateTime(value, userTimezone) },
        ...(hasMultiTenants ? [
          { field: 'tenant_name', headerName: i18n._('Tenant'), priority: 13, width: 150 },
        ] : []),
        ...(hasMultiWorkflows ? [
          { field: 'workflow_translated', headerName: i18n._('Type'), priority: 14, flex: 1 },
        ] : []),
        { field: 'creator_alias', headerName: i18n._('Origin'), priority: 6, flex: 1 },
        { field: 'creator_local', headerName: i18n._('User'), priority: 6, flex: 1 },
        { field: 'patient_first_name', headerName: i18n._('First name'), priority: 3, flex: 1, width: 150 },
        { field: 'patient_last_name', headerName: i18n._('Last name'), priority: 4, flex: 1, width: 150 },
        {
          field: 'station_vitals',
          headerName: i18n._('Exams'),
          priority: 15,
          flex: 1,
          filterOperators: [examAnyOfOperator],
          renderCell: (params) => <CellExamIcons exams={params.value} />,
        },
        { field: 'last_event', headerName: i18n._('Last event'), priority: 7, flex: 1, filterable: false, valueFormatter: (value) => formatDateTime(value, userTimezone) },
        { field: 'status_translated', headerName: i18n._('Status'), priority: 8, renderCell: (params) => <CellChip value={params.value} /> },
        {
          field: 'color_code',
          headerName: i18n._('Color code'),
          renderHeader: () => null,
          priority: 9,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellColorCircle color={params.value} /></ClickableCell>,
        },
        {
          field: 'notes',
          headerName: i18n._('Notes'),
          renderHeader: () => null,
          priority: 10,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellIcon value={params.value} iconName='notes' userTimezone={userTimezone} /></ClickableCell>,
        },
        {
          field: 'reminder_at',
          headerName: i18n._('Reminder'),
          renderHeader: () => null,
          priority: 11,
          filterable: false,
          width: 20,
          resizable: false,
          renderCell: (params) => <ClickableCell onClick={() => onOpenReminderModal(params.row.consultation_id)}><CellIcon value={params.value} iconName='reminder' userTimezone={userTimezone} /></ClickableCell>,
        },
        { field: 'links', headerName: i18n._('PDF'), renderHeader: () => null, priority: 12, sortable: false, filterable: false, width: 20, resizable: false, renderCell: (params) => <CellPdfButton links={params.value} /> },
        {
          field: 'modal',
          headerName: i18n._('Actions'),
          renderHeader: () => null,
          priority: 5,
          sortable: false,
          filterable: false,
          resizable: false,
          width: 20,
          renderCell: (params) => <ReminderModal row={params.row} showColorAndNotes />,
        },
      );
      break;
  }
  // Apply LinkCell to all columns that don't already have a renderCell defined.
  // This allows for context-menu, without wrapping the entire row and overlapping other clickable cells.
  const columnsWithLinkCells = columns.map((col) => {
    if (col.renderCell === undefined) {
      return { ...col, renderCell: (params) => <LinkCell {...params} /> };
    }
    return col;
  });
  return columnsWithLinkCells;
};

export const formatDateTime = (dateTimeStr: string, userTimezone: string) => {
  if (!dateTimeStr) {
    return '';
  }
  return dayjs(dateTimeStr).tz(userTimezone).format('DD MMMM YYYY HH:mm:ss'); // ex: 31 march 2024, 10:05:30
};

// Responsive columns algorithm - Auto-hide columns based on their priority and the current available table width.
// Tries to mirror DataTables Responsive behaviour:
//   - priority 1  → always visible, never culled.
//   - priority > 1 → culled from highest value first, rightmost-first if the same priority.
//   - Each cullable column gets its own evenly-spaced breakpoint across [0, maxTableDefaultWidth].
// Returns a GridColumnVisibilityModel so DataGrid's column management panel still lists hidden columns,
// letting users manually re-enable them.
export const getResponsiveColumnVisibilityModel = ({ columns, currentSize }: { columns: ExtendedGridColDef[], currentSize: number }): GridColumnVisibilityModel => {
  const maxTableDefaultWidth = 1500; // The screen width at which we except all columns to be shown.

  // Collect columns that can be culled (priority > 1), preserving original index.
  const cullableColumns = columns
    .map((col, index) => ({ col, index }))
    .filter(({ col }) => col.priority !== undefined && col.priority > 1);

  // Cull order: highest priority value first; rightmost column first within the same priority.
  const cullOrder = [...cullableColumns].sort(
    (a, b) => b.col.priority! - a.col.priority! || b.index - a.index,
  );

  // Assign each cullable column its own breakpoint, evenly spaced across the full width.
  const n = cullOrder.length;
  const step = maxTableDefaultWidth / n;
  const breakpointMap = new Map<number, number>(); // original column index → breakpoint
  cullOrder.forEach(({ index }, cullIndex) => {
    breakpointMap.set(index, maxTableDefaultWidth - cullIndex * step);
  });

  const visibilityModel: GridColumnVisibilityModel = {};
  columns.forEach((column, index) => {
    if (column.priority !== undefined && column.priority > 1) {
      // Only include cullable columns in the model; omitting a field means visible=true by default.
      visibilityModel[column.field] = currentSize >= breakpointMap.get(index)!;
    }
  });
  return visibilityModel;
};
